import { useMelwaysInfo } from '@seek/melways-react';
import type { MelwaysZone } from '@seek/melways-sites';
import { MarketSelector, type Market } from '@ts-internal/shared-lib';
import { useTranslations } from '@vocab/react';
import { Box, Stack } from 'braid-design-system';

import { useGetSuggestionsForLocationLazyQuery } from 'src/graphql/Queries/getSuggestionsForLocation.generated';

import { DEFAULT_SUGGESTION_COUNT } from '../../constants';
import type { Suggestion } from '../../types';
import { FilterAccordion } from '../AccordionFilter/FilterAccordion';
import { Filter } from '../Filter/Filter';

import translations from './.vocab';

import { inputParent } from './LocationFilter.css';

type LocationFilterProps = {
  expanded: boolean;
  selectedItems: Suggestion[];
  onChange: (newSelectedItems: Suggestion[]) => void;
  onExpandedToggle: (expanded: boolean) => void;
  marketState: {
    value: Market;
    onChange: (marketSelected: Market) => void;
  };
};

export const LocationFilter = ({
  expanded,
  selectedItems,
  onChange,
  onExpandedToggle,
  marketState: { value: market, onChange: onMarketChange },
}: LocationFilterProps) => {
  const { language } = useMelwaysInfo();
  const zone = getMelwaysZoneByMarket(market);
  const { t } = useTranslations(translations);

  const [triggerGetSuggestions, { data }] =
    useGetSuggestionsForLocationLazyQuery({
      // Solution: not cache this query at all.
      fetchPolicy: 'no-cache',
    });

  const suggestions = (data?.mkLocationSuggestions || []).map(
    ({ id, text }) => ({
      id: id.toString(),
      name: text,
    }),
  );

  const handleSearchTextChange = (newSearchText: string) => {
    if (newSearchText) {
      const kind = [
        'Country',
        'Region',
        'State',
        'SEEKLocation',
        'SEEKArea',
        'Suburb',
      ];

      triggerGetSuggestions({
        variables: {
          searchText: newSearchText,
          session: '',
          kind,
          zone,
          count: DEFAULT_SUGGESTION_COUNT,
          languageCode: language,
          countries: [market],
        },
      });
    }
  };

  return (
    <FilterAccordion
      id="location-accordion-filter"
      expanded={expanded}
      label={t('LOCATION')}
      selectedItems={selectedItems}
      onToggle={onExpandedToggle}
    >
      <Stack space="small">
        <Box className={inputParent}>
          <MarketSelector
            marketSelected={market}
            onMarketChange={onMarketChange}
            size="small"
          />
        </Box>
        <Filter
          id="location-filter"
          label=""
          placeholder={t('LOCATION_FILTER_PLACEHOLDER')}
          suggestions={suggestions}
          selectedItems={selectedItems}
          onDebouncedSearchTextChange={handleSearchTextChange}
          onChange={onChange}
        />
      </Stack>
    </FilterAccordion>
  );
};

const getMelwaysZoneByMarket = (market: Market): MelwaysZone => {
  switch (market) {
    case 'NZ':
      return 'anz-2';
    case 'HK':
      return 'asia-1';
    case 'TH':
      return 'asia-3';
    case 'ID':
      return 'asia-4';
    case 'MY':
      return 'asia-5';
    case 'PH':
      return 'asia-6';
    case 'SG':
      return 'asia-7';
    default:
      return 'anz-1';
  }
};
